import type { FoodItem, MealEstimate } from "../types";
import type { NutrientPanel } from "../nutrients";
import { cacheGet, cacheSet, DAY_MS } from "../cache";

interface OFFProduct {
  product_name?: string;
  brands?: string;
  serving_size?: string;
  serving_quantity?: number;
  image_url?: string;
  image_small_url?: string;
  image_thumb_url?: string;
  image_front_url?: string;
  image_front_small_url?: string;
  image_front_thumb_url?: string;
  // OFF's nutriments object is loose — every nutrient is optional and exists
  // in 4 flavors (per-100g, per-serving, unit, label). We only read what
  // we need; missing fields stay undefined.
  nutriments?: Record<string, number | string | undefined>;
}

// Map from OFF nutriment keys → our canonical NutrientPanel keys.
// OFF reports vitamins in grams (vitamin-c_100g = 0.06 means 60mg), so we
// multiply by the conversion factor below to get our preferred unit.
// Reference: https://wiki.openfoodfacts.org/Nutrition_data
const OFF_NUTRIENT_MAP: Array<{
  off: string;
  ours: keyof NutrientPanel;
  /** Multiply OFF value (always in grams) by this to get our unit. */
  factor: number;
}> = [
  // Vitamins — OFF returns in grams; our units vary
  { off: "vitamin-a", ours: "vit_a_ug", factor: 1_000_000 }, // g → μg
  { off: "vitamin-b1", ours: "vit_b1_mg", factor: 1_000 },
  { off: "vitamin-b2", ours: "vit_b2_mg", factor: 1_000 },
  { off: "vitamin-pp", ours: "vit_b3_mg", factor: 1_000 }, // OFF uses "pp" for niacin
  { off: "vitamin-b6", ours: "vit_b6_mg", factor: 1_000 },
  { off: "vitamin-b9", ours: "vit_b9_ug", factor: 1_000_000 },
  { off: "folates", ours: "vit_b9_ug", factor: 1_000_000 }, // alt key
  { off: "vitamin-b12", ours: "vit_b12_ug", factor: 1_000_000 },
  { off: "vitamin-c", ours: "vit_c_mg", factor: 1_000 },
  { off: "vitamin-d", ours: "vit_d_ug", factor: 1_000_000 },
  { off: "vitamin-e", ours: "vit_e_mg", factor: 1_000 },
  { off: "vitamin-k", ours: "vit_k_ug", factor: 1_000_000 },
  // Minerals
  { off: "iron", ours: "iron_mg", factor: 1_000 },
  { off: "calcium", ours: "calcium_mg", factor: 1_000 },
  { off: "magnesium", ours: "magnesium_mg", factor: 1_000 },
  { off: "zinc", ours: "zinc_mg", factor: 1_000 },
  { off: "potassium", ours: "potassium_mg", factor: 1_000 },
  { off: "sodium", ours: "sodium_mg", factor: 1_000 },
  { off: "phosphorus", ours: "phosphorus_mg", factor: 1_000 },
  { off: "copper", ours: "copper_mg", factor: 1_000 },
  { off: "manganese", ours: "manganese_mg", factor: 1_000 },
  { off: "selenium", ours: "selenium_ug", factor: 1_000_000 },
  { off: "iodine", ours: "iodine_ug", factor: 1_000_000 },
  // Other — already in grams
  { off: "sugars", ours: "sugar_g", factor: 1 },
  { off: "saturated-fat", ours: "saturated_fat_g", factor: 1 }
];

function extractNutrients(
  nutriments: Record<string, number | string | undefined> | undefined,
  hasServing: boolean
): NutrientPanel | undefined {
  if (!nutriments) return undefined;
  const out: NutrientPanel = {};
  const suffix = hasServing ? "_serving" : "_100g";
  for (const { off, ours, factor } of OFF_NUTRIENT_MAP) {
    const raw = nutriments[`${off}${suffix}`];
    const v = typeof raw === "number" ? raw : raw !== undefined ? parseFloat(String(raw)) : NaN;
    if (Number.isFinite(v) && v > 0) {
      // Don't overwrite a value we already set (e.g. vit_b9 from both
      // "vitamin-b9" and "folates" — take whichever comes first).
      if (out[ours] === undefined) {
        out[ours] = Math.round(v * factor * 10) / 10;
      }
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export interface BarcodeLookupResult {
  estimate: MealEstimate;
  imageUrl?: string;
  gramWeight?: number;
}

export interface SearchResult {
  code: string;
  name: string;
  brand?: string;
  imageUrl?: string;
  estimate: MealEstimate;
  /** Grams in 1 serving — enables the Portions/Grams toggle when present. */
  gramWeight?: number;
}

export async function searchProducts(query: string, limit = 5): Promise<SearchResult[]> {
  const q = query.trim();
  if (!q) return [];

  const cacheKey = `off.search.${q.toLowerCase()}.${limit}`;
  const cached = cacheGet<SearchResult[]>(cacheKey);
  if (cached) return cached;

  // world endpoint with a US country filter — more reliable than us.openfoodfacts.org
  // (which 503s intermittently) and still biases results to US-distributed products.
  const params = new URLSearchParams({
    search_terms: q,
    countries_tags_en: "united-states",
    fields:
      "code,product_name,brands,nutriments,image_small_url,image_front_small_url,serving_size,serving_quantity",
    page_size: String(limit),
    sort_by: "unique_scans_n"
  });
  const url = `https://world.openfoodfacts.org/api/v2/search?${params.toString()}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Open Food Facts ${res.status}`);
  const data = await res.json();
  const products = (data.products ?? []) as Array<OFFProduct & { code?: string }>;
  const results: SearchResult[] = products
    .filter((p) => p.product_name && p.nutriments && (p.nutriments["energy-kcal_100g"] ?? p.nutriments["energy-kcal_serving"]))
    .map((p) => ({
      code: p.code ?? "",
      name: p.product_name ?? "Unknown",
      brand: p.brands ?? undefined,
      imageUrl:
        p.image_front_small_url ??
        p.image_small_url ??
        undefined,
      estimate: productToEstimate(p),
      gramWeight: p.serving_quantity ? Number(p.serving_quantity) : undefined
    }));
  cacheSet(cacheKey, results, DAY_MS);
  return results;
}

export async function lookupBarcode(code: string): Promise<BarcodeLookupResult> {
  const cacheKey = `off.product.${code}`;
  const cached = cacheGet<BarcodeLookupResult>(cacheKey);
  if (cached) return cached;

  const url = `https://world.openfoodfacts.org/api/v2/product/${encodeURIComponent(code)}.json`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Open Food Facts ${res.status}`);
  const data = await res.json();
  if (data.status !== 1 || !data.product) {
    throw new Error(`Product not found for barcode ${code}. Open Food Facts may not have this item yet.`);
  }
  const p = data.product as OFFProduct;
  const result: BarcodeLookupResult = {
    estimate: productToEstimate(p),
    imageUrl:
      p.image_front_small_url ??
      p.image_small_url ??
      p.image_front_url ??
      p.image_url ??
      undefined,
    gramWeight: p.serving_quantity ? Number(p.serving_quantity) : undefined
  };
  cacheSet(cacheKey, result, DAY_MS);
  return result;
}

function productToEstimate(p: OFFProduct): MealEstimate {
  const n = p.nutriments ?? {};
  const numAt = (key: string): number | undefined => {
    const v = n[key];
    return typeof v === "number" ? v : v !== undefined ? parseFloat(String(v)) : undefined;
  };
  const hasServing = (numAt("energy-kcal_serving") ?? 0) > 0;

  const portion = hasServing
    ? p.serving_size ?? (p.serving_quantity ? `${p.serving_quantity} g (1 serving)` : "1 serving")
    : "100 g";

  const item: FoodItem = {
    name: [p.brands, p.product_name].filter(Boolean).join(" — ") || "Unknown product",
    portion,
    calories: (hasServing ? numAt("energy-kcal_serving") : numAt("energy-kcal_100g")) ?? 0,
    protein_g: (hasServing ? numAt("proteins_serving") : numAt("proteins_100g")) ?? 0,
    carbs_g: (hasServing ? numAt("carbohydrates_serving") : numAt("carbohydrates_100g")) ?? 0,
    fat_g: (hasServing ? numAt("fat_serving") : numAt("fat_100g")) ?? 0,
    fiber_g: hasServing ? numAt("fiber_serving") : numAt("fiber_100g"),
    nutrients: extractNutrients(n, hasServing)
  };

  return {
    items: [item],
    totals: {
      calories: item.calories,
      protein_g: item.protein_g,
      carbs_g: item.carbs_g,
      fat_g: item.fat_g,
      fiber_g: item.fiber_g ?? 0
    },
    confidence: "high",
    clarifying_questions: [],
    notes: hasServing
      ? "From Open Food Facts (per serving). Adjust portion if you had more or less."
      : "From Open Food Facts (per 100 g). Adjust portion to match what you actually had."
  };
}
